import React, { useCallback, useRef, useMemo } from 'react'

import ClientSideTable from '@/components/Table/ClientSideTable'
import QuotationDetailTableToolbar from '@/features/offers/offer-detail/components/QuotationDetailTableToolbar'
import { emptyQuotationTemplateRow } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import {
  createQuotationDetailUpdateHandler,
  downloadQuotationDetailExcel,
  getDefaultQuotationCategoryAndSAPMaterialNumber,
} from '@/features/offers/offer-detail/helper/quotationDetailExcel.helper'
import { getQuotationTemplateDetailColumns } from '../quotationTeamplateDetailColumns'
import useZZZ6 from '@/features/zzz6/useZZZ6'
import { excelFileTypes } from '@/utils/constants'
import { getFormattedDataFromExcelFile } from '@/utils/fileHelper'
import { Flex } from '@chakra-ui/react'
import { CostBudgetType, getQuotationDetailTableColumnIds } from '@eapproval/offer-shared'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

const CostBudgetTemplateTable = ({ costBudgetIndex, tenderDocumentIndex, disabled = false }) => {
  const { setValue, getValues } = useFormContext()
  const importExcelRef = useRef(null)
  const { useFetchAllZZZ6s, useFetchZZZ6CategoryOptions } = useZZZ6()
  const quotationTemplateBasePath = `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.quotationTemplate.details`

  const { append: appendQuotationTemplateRows, remove: removeQuotationTemplateRows } = useFieldArray({
    name: quotationTemplateBasePath,
  })
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })
  const watchedQuotationTemplateDetails = useWatch({
    name: quotationTemplateBasePath,
  })
  const quotationTemplateDetails = useMemo(
    () => watchedQuotationTemplateDetails ?? [],
    [watchedQuotationTemplateDetails]
  )

  const disableCategory = [
    CostBudgetType.SUPPLY_ENGINEERING_SERVICE,
    CostBudgetType.SUPPLY_MAINTENANCE_SERVICE,
  ].includes(costBudgetType)

  const { data: zzz6Response } = useFetchAllZZZ6s({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })
  const { data: categoryOptions = [] } = useFetchZZZ6CategoryOptions({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const handleImportExcel = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    const tableColumns = getQuotationDetailTableColumnIds({
      costBudgetType,
      isTemplate: true,
      canRemove: false,
    })

    const dataRows = await getFormattedDataFromExcelFile(file, tableColumns)

    setValue(quotationTemplateBasePath, dataRows, { shouldDirty: true })

    if (importExcelRef.current) importExcelRef.current.value = ''
  }

  const handleRemoveQuotationTemplateRow = (index) => {
    removeQuotationTemplateRows(index)
  }

  const columnDef = getQuotationTemplateDetailColumns({
    costBudgetType,
    handleRemove: handleRemoveQuotationTemplateRow,
    isTemplate: true,
    disabled: disabled,
    categoryOptions,
    disableCategory,
  })

  const handleDownloadQuotationDetail = useCallback(async () => {
    try {
      await downloadQuotationDetailExcel({
        costBudgetType,
        isTemplate: true,
        canRemove: false,
        rows: quotationTemplateDetails,
      })
    } catch (err) {
      console.log(err)
    }
  }, [costBudgetType, quotationTemplateDetails])

  const handleUpdateQuotationTemplateDetails = createQuotationDetailUpdateHandler({
    disabled,
    setValue,
    getValues,
    zzz6Response,
    costBudgetType,
    getRowPath: (rowIndex) => `${quotationTemplateBasePath}.${rowIndex}`,
    calculateTotalPrice: false,
  })

  const handleAddQuotationTemplateRow = () => {
    const { category: defaultCategory, SAPMaterialNumber: defaultSAPMaterialNumber } =
      getDefaultQuotationCategoryAndSAPMaterialNumber({
        costBudgetType,
        zzz6Response,
      })

    appendQuotationTemplateRows({
      ...emptyQuotationTemplateRow,
      category: defaultCategory,
      SAPMaterialNumber: defaultSAPMaterialNumber,
    })
  }

  return (
    <Flex direction={'column'} gap="3">
      <QuotationDetailTableToolbar
        importExcelRef={importExcelRef}
        excelFileTypes={excelFileTypes}
        disabled={disabled}
        onImportExcel={handleImportExcel}
        onDownload={handleDownloadQuotationDetail}
        onAddRow={handleAddQuotationTemplateRow}
      />

      <Flex w="100%" overflowX={'auto'} direction={'column'} gap="3">
        <ClientSideTable
          columnDef={columnDef}
          data={quotationTemplateDetails}
          disabled={disabled}
          updateData={handleUpdateQuotationTemplateDetails}
        />
      </Flex>
    </Flex>
  )
}

export default CostBudgetTemplateTable
